import {MouseEvent, useCallback, useState} from 'react';
import {useDispatch} from 'react-redux';
import {matchPath, useLocation, useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';

import {fetchMe} from 'entities/user';
import {TypedDispatch} from 'shared/model/types';

import {tagsAPI} from '../api/tagsAPI';

type MonitorRequest = () => Promise<unknown>;

interface IMonitorMutationParams {
    request: MonitorRequest,
    isLoading: boolean,
    handleClose: () => void,
    successText: string,
}

export const useMonitorPause = ({request, isLoading, handleClose, successText}: IMonitorMutationParams) => {
    const dispatch = useDispatch<TypedDispatch>();

    const pause = useCallback(async() => {
        await request();
        toast.info(successText);
        dispatch(fetchMe());
        handleClose();
    }, [request, successText, dispatch, handleClose]);

    return {pause, isLoading};
};

export const useMonitorResume = ({request, isLoading, handleClose, successText}: IMonitorMutationParams) => {
    const dispatch = useDispatch<TypedDispatch>();
    const [isExceededOpen, setIsExceededOpen] = useState(false);

    const resume = useCallback(async() => {
        try {
            await request();
            toast.info(successText);
            dispatch(fetchMe());
            handleClose();
        } catch (error) {
            handleClose();
            setIsExceededOpen(true);
        }
    }, [request, successText, dispatch, handleClose]);

    const closeExceeded = useCallback(() => setIsExceededOpen(false), []);

    return {resume, isLoading, isExceededOpen, closeExceeded};
};

interface IMonitorDeleteParams extends IMonitorMutationParams {
    listUrl: string,
    detailsRoute: string,
    withTags?: boolean,
    onDeleted?: (_dispatch: TypedDispatch) => void,
}

export const useMonitorDelete = ({
    request,
    isLoading,
    handleClose,
    successText,
    listUrl,
    detailsRoute,
    withTags = false,
    onDeleted,
}: IMonitorDeleteParams) => {
    const dispatch = useDispatch<TypedDispatch>();
    const navigate = useNavigate();
    const {pathname} = useLocation();
    const isOnDetailsPage = matchPath(detailsRoute, pathname) !== null;

    const remove = useCallback(async() => {
        if (isOnDetailsPage) {
            navigate(listUrl);
        }
        await request();
        onDeleted?.(dispatch);
        if (withTags) {
            dispatch(tagsAPI.util.invalidateTags(['Tags']));
        }
        toast.info(successText);
        dispatch(fetchMe());
        handleClose();
    }, [isOnDetailsPage, navigate, listUrl, request, onDeleted, dispatch, withTags, successText, handleClose]);

    return {remove, isLoading};
};

interface IMonitorToggleParams {
    isActive: boolean,
    isPaused: boolean,
    pause: MonitorRequest,
    resume: MonitorRequest,
    pausedText: string,
    resumedText: string,
    onLimit: () => void,
    onClick?: () => void,
}

export const useMonitorToggle = ({
    isActive,
    isPaused,
    pause,
    resume,
    pausedText,
    resumedText,
    onLimit,
    onClick,
}: IMonitorToggleParams) => {
    const dispatch = useDispatch<TypedDispatch>();

    return useCallback(async(event?: MouseEvent) => {
        event?.stopPropagation();
        onClick?.();

        try {
            if (isActive) {
                await pause();
                toast.info(pausedText);
            } else if (isPaused) {
                await resume();
                toast.info(resumedText);
            } else {
                return;
            }
            dispatch(fetchMe());
        } catch (error) {
            onLimit();
        }
    }, [onClick, isActive, isPaused, pause, pausedText, resume, resumedText, dispatch, onLimit]);
};
